import { useCallback, useRef } from 'react'

import { PREVIEW_MODE_STRATEGY } from '../registries/outlinePreviewRegistry.js'
import { HERO_OUTLINE } from '../constants/heroOutline.js'
import { OUTLINE_PREVIEW_MODE } from '../constants/outlinePreviewMode.js'
import { SVG_OUTLINE_CLASS } from '../constants/svgOutlineClasses.js'

// ── ID parsing ─────────────────────────────────────────────────────────────────

/**
 * Returns true when a raw SVG path `id` value matches the canonical tier format:
 * `{building_id_cad}-{zfill_tier}`, e.g. `"01-01"`, `"0A-10"`, `"KR-09"`.
 *
 * Non-canonical integer IDs (e.g. `"9"`, `"16"`) are NOT matched here.
 * Single-building projects have them rewritten to canonical format by
 * `normalizeVisPathIds()` before hooks run.
 * Multi-building shared outlines retain their plain-integer ID — those
 * are handled separately by `isLegacyTierPathId`.
 *
 * @param {string} id
 * @returns {boolean}
 */
export function isTierPathId(id) {
  if (typeof id !== 'string') return false
  return /^[0-9A-Za-z]{2}-\d{2,}$/.test(id)
}

/**
 * Returns true for legacy plain-integer tier path IDs (e.g. `"0"`, `"3"`, `"12"`).
 *
 * These appear in older SVGs where a single outline covers the same floor
 * across all buildings. The ID equals the `ref_tier` integer value.
 *
 * Zero-padded numerics like `"00"` or `"08"` are NOT matched here —
 * those are building `id_cad` values (padded format).
 *
 * @param {string} id
 * @returns {boolean}
 */
export function isLegacyTierPathId(id) {
  if (typeof id !== 'string') return false
  // Must be pure digits, and if multi-char must NOT start with '0' (that's a building ID)
  return /^\d+$/.test(id) && (id.length === 1 || id[0] !== '0')
}

// ── Pure factory (testable without React) ────────────────────────────────────

/**
 * Creates a `wireListeners` function for tier outline interaction.
 *
 * @param {object}   params — same shape as `useSvgTierInteraction` params
 * @param {object}   refs
 * @param {{ current: string|null }} refs.lastClickedRef
 * @param {{ current: Element|null }} refs.previewedPathRef
 * @returns {(containerEl: Element|null) => () => void}
 */
export function createTierWireListeners(
  { 
    areSoldOutTiersActive,
    soldUnitsAccessible,
    tiersData, 
    buildingsData, 
    activeTier, 
    activeBuilding, 
    onOutlineClick, 
    onPreviewChange, 
    deviceLayout, 
    currentFrameRef, 
    isDraggingRef, 
    // previewedId = null 
  },
  refs,
) {
  return (containerEl) => {
    if (!containerEl) return () => {}

    const { lastClickedRef, previewedPathRef } = refs
    const buildingById = new Map((buildingsData ?? []).map(b => [b.id_cad, b]))

    // Returns true when the path is inside the currently active g[data-frame] group,
    // or when the SVG has no frame groups (VisualizationView / flat outline).
    const isInActiveFrame = (el) => {
      if (!currentFrameRef) return true
      const group = el.closest('g[data-frame]')
      if (!group) return true
      return parseInt(group.dataset.frame, 10) === currentFrameRef.current
    }
    // SVG path IDs use the compact id_cad format: "{building_id}-{zfill(ref_tier)}" 
    // omitting gate part e.g. "0A-03", "0B-16".
    // tiers in buildings.json may have legacy `id_cad` values with the placeholder gate segment (e.g. "0A-ZZ-03")
    // TODO: once all tier data is normalized to the compact format, just use id from data and not building_id + ref_tier
    
    // const byIdCad      = new Map((tiersData ?? []).map(t => [t.id_cad, t]))
    const byIdCad      = new Map()
    for (const t of (tiersData ?? [])) {
      const visId = `${t.building_id}-${String(t.ref_tier).padStart(2, '0')}`
      byIdCad.set(visId, t)
    }

    // Legacy shared outlines: one path covers all buildings' floor N,  id = String(ref_tier).
    // First tier candidate per ref_tier is used as representative (building shown on click).
    const byLegacyRefTier = new Map()
    for (const t of (tiersData ?? [])) {
      const key = String(t.ref_tier)
      if (!byLegacyRefTier.has(key)) {
        byLegacyRefTier.set(key, { tier: t, building: buildingById.get(t.building_id) })
      }
    }

    const isMobile     = deviceLayout?.isMobile ?? false

    const paths = Array.from(containerEl.querySelectorAll('path[id]')).filter(
      el => (isTierPathId(el.id) && byIdCad.has(el.id)) ||
            (isLegacyTierPathId(el.id) && byLegacyRefTier.has(el.id))
    )

    const controller = new AbortController()
    const { signal } = controller

    for (const path of paths) {
      const isLegacy = isLegacyTierPathId(path.id) && !isTierPathId(path.id)
      const { tier, building } = isLegacy
        ? byLegacyRefTier.get(path.id)
        : { tier: byIdCad.get(path.id), building: buildingById.get(byIdCad.get(path.id)?.building_id) }

      // Legacy shared outlines span all buildings — active when ref_tier matches, regardless of building.
      // Canonical outlines are scoped: both ref_tier AND building must match.
      path.classList.toggle(SVG_OUTLINE_CLASS.ACTIVE,
        isLegacy
          ? tier.ref_tier === activeTier
          : tier.ref_tier === activeTier && (tier.building_id ?? null) === (activeBuilding ?? null)
      )

      const mode = resolveTierPreviewMode({
        tier,
        building,
        areSoldOutTiersActive,
        soldUnitsAccessible,
      })
      const strategy = PREVIEW_MODE_STRATEGY[mode]
      const isInteractive = strategy?.isInteractive ?? false

      path.style.cursor = isInteractive ? 'pointer' : 'default'

      if (!isMobile) {
        path.addEventListener('mouseover', (e) => {
          if (isDraggingRef?.current) return
          if (!isInActiveFrame(path)) return
          path.classList.add(SVG_OUTLINE_CLASS.HOVERED)
          onPreviewChange?.({ type: HERO_OUTLINE.TIERS, tier, building, mode, cursorPos: { x: e.clientX, y: e.clientY } })
        }, { signal })

        path.addEventListener('mouseleave', () => {
          if (!isInActiveFrame(path)) return
          path.classList.remove(SVG_OUTLINE_CLASS.HOVERED)
          onPreviewChange?.(null)
        }, { signal })

        if(isInteractive) {
          path.addEventListener('click', () => {
            if (!isInActiveFrame(path)) return
            onOutlineClick?.(mode, { 
              type: HERO_OUTLINE.TIERS, 
              building,
              tier,
              mode,
            })
          }, { signal })
        }

      } else {
        path.addEventListener('click', (e) => {
          if (!isInActiveFrame(path)) return
          if (lastClickedRef.current === path.id) {
            if (isInteractive) {
              onPreviewChange?.(null)
              onOutlineClick?.(mode, { 
                type: HERO_OUTLINE.TIERS, 
                building,
                tier,
                mode,
              })
            }
          } else {
            lastClickedRef.current = path.id
            onPreviewChange?.({
              type: HERO_OUTLINE.TIERS,
              tier,
              building,
              cursorPos: { x: e.clientX, y: e.clientY },
              anchorRect: path.getBoundingClientRect(),
            })
          }
        }, { signal })
      }
    }

    const handlePreviewChangeSync = (e) => {
      const newIdCad = e.detail

      paths.forEach(path => {
        if (newIdCad && path.id === newIdCad) {
          path.classList.add(SVG_OUTLINE_CLASS.PREVIEW)
          lastClickedRef.current = newIdCad // double-tap update
        } else {
          path.classList.remove(SVG_OUTLINE_CLASS.PREVIEW)
        }
      })

    }

    containerEl.addEventListener('bim:preview-change', handlePreviewChangeSync)

    return () => {
      controller.abort()
      containerEl.removeEventListener('bim:preview-change', handlePreviewChangeSync)
      if (previewedPathRef.current) previewedPathRef.current.classList.remove(SVG_OUTLINE_CLASS.PREVIEW)
      previewedPathRef.current = null
      lastClickedRef.current = null
    }
  }
}

// ── Hook (thin React wrapper) ─────────────────────────────────────────────────

/**
 * useSvgTierInteraction
 *
 * Manages imperative event listeners on SVG tier paths rendered by
 * `SvgOutlineLayer`. Returns a `wireListeners` function to be called from
 * `SvgOutlineLayer`'s internal `useEffect` after the SVG mounts.
 *
 * @param {object}          params
 * @param {object[]}        params.tiersData
 * @param {object[]}        params.buildingsData
 * @param {number|null}     params.activeTier
 * @param {string|null}     params.activeBuilding
 * @param {function}        params.onTierClick
 * @param {function}        [params.onPreviewChange]
 * @param {{ isMobile: boolean }} [params.deviceLayout]
 *
 * @returns {{ wireListeners: (containerEl: Element|null) => () => void }}
 */
export function useSvgTierInteraction(params) {
  const lastClickedRef   = useRef(null)
  const previewedPathRef = useRef(null)

  const wireListeners = useCallback(
    (containerEl) => createTierWireListeners(params, { lastClickedRef, previewedPathRef })(containerEl),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      params.tiersData, 
      params.buildingsData, 
      params.activeTier, 
      params.activeBuilding, 
      params.onTierClick, 
      params.onPreviewChange, 
      params.deviceLayout, 
    ],
    // params.currentFrameRef and params.isDraggingRef are stable ref objects — intentionally excluded from deps
  )

  return { wireListeners }
}


/**
 * Resolves preview mode and interactivity of a tier outline.
 *
 * Hierarchy:
 * 1. Phasing of tiers building (currently_on_sale === false) -> inherited by the tier
 * 2. Active flag from CMS (is_active === false)
 * 3. Sold-out status (isSoldOut) with CMS setting
 * 4. Default mode (NORMAL_OUTLINE)
 *
 * @param {object} params
 * @param {object|null} params.tier                   - Tier object from tiersData
 * @param {object|null} params.building               - tiers Building object from buildingsData
 * @param {boolean} [params.areSoldOutTiersActive]    - CMS Flag: are fully sold-out tiers visible/accessible in app
 * @param {boolean} [params.soldUnitsAccessible]      - CMS Flag: are sold products viewable - overrides areSoldOutTiersActive
 * @returns {string} Value from OUTLINE_PREVIEW_MODE
 */
export function resolveTierPreviewMode({
  tier,
  building = null,
  areSoldOutTiersActive = false,
  soldUnitsAccessible = false,
}) {
  if (!tier) return OUTLINE_PREVIEW_MODE.DISABLED_INACTIVE

  if (building && building.currently_on_sale === false) {
    if (building.allow_inquiry)  return OUTLINE_PREVIEW_MODE.OTHER_STAGE_INQUIRY
    if (building.redirect_to_app) return OUTLINE_PREVIEW_MODE.OTHER_STAGE_REDIRECT
    return OUTLINE_PREVIEW_MODE.OTHER_STAGE
  }

  if (tier.is_active === false) {
    return OUTLINE_PREVIEW_MODE.DISABLED_INACTIVE
  }

  if (tier.isSoldOut && !areSoldOutTiersActive && !soldUnitsAccessible) {
    return OUTLINE_PREVIEW_MODE.DISABLED_SOLD_OUT
  }

  return OUTLINE_PREVIEW_MODE.NORMAL_OUTLINE
}